import React, { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import type { AuditLogEntry } from "@cms/types";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { useListControls } from "@cms/portal-lib/list-controls";
import { formatTimestamp } from "@cms/portal-lib/format";
import { Badge } from "@cms/ui/components/badge";
import { Input } from "@cms/ui/components/input";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@cms/ui/components/table";
import { PaginationControls } from "@cms/ui/components/pagination-controls";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@cms/ui/components/select";
import {
  activityQueryString,
  fetchActivityPage,
  formatCategory,
  formatDetail,
  NO_ACTIVITY_FILTERS,
  type ActivityFilters,
} from "../lib/activity";
import type { AdminOutletContext } from "../lib/outlet-context";

// Radix Select items can't have an empty value, so "all" stands in for "".
const ALL = "all";

function FilterSelect({ value, onChange, placeholder, options }: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
}) {
  return (
    <Select value={value || ALL} onValueChange={(next: string) => onChange(next === ALL ? "" : next)}>
      <SelectTrigger className="w-44" aria-label={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function categoryBadgeVariant(category: string) {
  if (category === "act_as") return "warning";
  if (category === "billing") return "success";
  return "secondary";
}

function ActivityRow({ entry }: { entry: AuditLogEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(entry.detail_json);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <>
      <TableRow
        className={hasDetail ? "cursor-pointer" : undefined}
        onClick={hasDetail ? () => setOpen((prev) => !prev) : undefined}
      >
        <TableCell className="whitespace-nowrap text-muted-foreground">
          {formatTimestamp(entry.created_at)}
        </TableCell>
        <TableCell>
          <Badge variant={categoryBadgeVariant(entry.category)} className="capitalize">
            {formatCategory(entry.category)}
          </Badge>
        </TableCell>
        <TableCell className="font-medium">{entry.action}</TableCell>
        <TableCell>
          {entry.actor_user_id === "system"
            ? <span className="text-muted-foreground">System</span>
            : entry.actor_email ?? entry.actor_user_id}
        </TableCell>
        <TableCell>
          {entry.tenant_id
            ? <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">{entry.tenant_id}</code>
            : <span className="text-muted-foreground">—</span>}
        </TableCell>
        <TableCell className="w-8 text-right">
          {hasDetail && <Chevron className="h-4 w-4 text-muted-foreground" aria-label={open ? "Hide detail" : "Show detail"} />}
        </TableCell>
      </TableRow>
      {open && entry.detail_json && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/40">
            <pre className="overflow-x-auto whitespace-pre-wrap py-1 text-xs font-mono">
              {formatDetail(entry.detail_json)}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function ActivityPage() {
  const { api } = useOutletContext<AdminOutletContext>();
  const controls = useListControls();
  const [filters, setFilters] = useState<ActivityFilters>(NO_ACTIVITY_FILTERS);

  // Filter changes snap back to page 1, same as search, so the result window
  // never points past the end of the narrowed list.
  const setFilter = (key: keyof ActivityFilters) => (value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    controls.setPage(1);
  };

  const queryString = useMemo(
    () => activityQueryString(controls.queryString, filters),
    [controls.queryString, filters]
  );
  const query = useApiQuery(
    () => (api ? fetchActivityPage(api, queryString) : null),
    [api, queryString]
  );

  const entries = query.data?.entries ?? [];
  const total = query.data?.total ?? 0;
  const loaded = query.data !== null;
  const filtered = Boolean(controls.q || filters.category || filters.actor || filters.tenant);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Activity</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Audit log of admin and system actions across all clients.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1 basis-64">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={controls.search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => controls.setSearch(e.target.value)}
            placeholder="Search activity…"
            className="pl-8"
            aria-label="Search activity"
          />
        </div>
        <FilterSelect
          value={filters.category}
          onChange={setFilter("category")}
          placeholder="All types"
          options={(query.data?.categories ?? []).map((category) => ({
            value: category,
            label: formatCategory(category),
          }))}
        />
        <FilterSelect
          value={filters.actor}
          onChange={setFilter("actor")}
          placeholder="All users"
          options={(query.data?.actors ?? []).map((actor) => ({
            value: actor.user_id,
            label: actor.user_id === "system" ? "System" : actor.email ?? actor.user_id,
          }))}
        />
        <FilterSelect
          value={filters.tenant}
          onChange={setFilter("tenant")}
          placeholder="All clients"
          options={(query.data?.tenants ?? []).map((tenant) => ({ value: tenant, label: tenant }))}
        />
      </div>

      {query.error && <p className="mb-4 text-sm text-destructive">{query.error}</p>}

      {loaded && entries.length === 0 && total === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {filtered ? "No activity matches your filters" : "No activity recorded yet"}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Client</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => <ActivityRow key={entry.id} entry={entry} />)}
            </TableBody>
          </Table>
          <PaginationControls
            page={controls.page}
            total={total}
            perPage={controls.perPage}
            onPageChange={controls.setPage}
            onPerPageChange={controls.setPerPage}
            itemsLabel="entries"
            className="border-t px-4 py-3"
          />
        </div>
      )}
    </div>
  );
}
